import { useState } from 'react';

import styled from 'styled-components';

import Card from '../../components/card/Card';

import Table from '../../components/table/table';

import P from '../../components/texts/p/P';

import Span from '../../components/texts/span/Span';

import Tooltip from '../../components/tooltip/Tooltip';

import Icon from '../../components/icon/Icon';

import Button from '../../components/button/Button';

import { formatCurrency } from '../../utils/Masks';

import { toNumber } from '../../utils/Utils';

import { useClientTheme } from '../../context/ThemeContext';

import {
  getTranslation,
  transformObject,
  sumCategory,
  getMergedValues,
  DualRow,
} from './balanceShared';

const HEADER_BG = '#F5F7FA';

const HEADER_TEXT = '#231F20';

const TABLE_EDGE_PADDING = '16px';

const SummaryWrapper = styled.div`
  width: 100%;

  overflow-x: auto;

  .rdt_Table {
    min-width: 400px !important;
  }

  .rdt_TableCol {
    min-width: 100px !important;
  }

  .rdt_TableHead,
  .rdt_TableHead .rdt_TableHeadRow {
    background-color: ${HEADER_BG} !important;

    gap: 0 !important;
  }

  .rdt_TableHead .rdt_TableCol {
    background-color: ${HEADER_BG} !important;

    border: none !important;

    color: ${HEADER_TEXT} !important;

    font-weight: 600;
  }

  .rdt_TableHead .rdt_TableCol:first-child,
  .rdt_TableRow .rdt_TableCell:first-child {
    padding-left: ${TABLE_EDGE_PADDING} !important;
  }

  .rdt_TableHead .rdt_TableCol:last-child,
  .rdt_TableRow .rdt_TableCell:last-child {
    padding-right: ${TABLE_EDGE_PADDING} !important;
  }
`;

type SummaryRow = DualRow & {
  hasTopDivider?: boolean;

  expandableKey?: string;

  hideAmounts?: boolean;

  isSectionSubtotal?: boolean;

  isExerciseResult?: boolean;
};

type ExpandedState = Record<string, boolean>;

type ExpandableGroup = {
  key: string;

  label: string;

  category: string;

  skipFirst?: boolean;
};

type ResultSection = {
  category: string;

  subtotalLabel: string;

  getSubtotalPrev: (totals: any) => string | number;

  getSubtotalCurr: (totals: any) => string | number;
};

const EXPANDABLE_GROUPS: ExpandableGroup[] = [
  {
    key: 'current_assets',
    label: 'Activo Corriente',
    category: 'current_assets',
  },

  {
    key: 'non_current_assets',
    label: 'Activo No Corriente',
    category: 'non_current_assets',
  },

  {
    key: 'current_liabilities',
    label: 'Pasivo Corriente',
    category: 'current_liabilities',
  },

  {
    key: 'non_current_liabilities',
    label: 'Pasivo No Corriente',
    category: 'non_current_liabilities',
    skipFirst: true,
  },
];

const RESULT_SECTIONS: ResultSection[] = [
  {
    category: 'gross_result',

    subtotalLabel: 'Resultado bruto',

    getSubtotalPrev: (t) => t?.gross_result ?? 0,

    getSubtotalCurr: (t) => t?.gross_result ?? 0,
  },

  {
    category: 'operating_result',

    subtotalLabel: 'Resultado operativo',

    getSubtotalPrev: (t) => t?.operating_result ?? 0,

    getSubtotalCurr: (t) => t?.operating_result ?? 0,
  },

  {
    category: 'ordinary_result',

    subtotalLabel: 'Resultado ordinario',

    getSubtotalPrev: (t) => t?.ordinary_result ?? 0,

    getSubtotalCurr: (t) => t?.ordinary_result ?? 0,
  },

  {
    category: 'result_before_taxes',

    subtotalLabel: 'Resultado antes de impuestos',

    getSubtotalPrev: (t) =>
      t?.result_before_taxes?.total ??
      t?.result_before_taxes?.totalFormatted ??
      t?.result_before_taxes ??
      0,

    getSubtotalCurr: (t) =>
      t?.result_before_taxes?.total ??
      t?.result_before_taxes?.totalFormatted ??
      t?.result_before_taxes ??
      0,
  },
];

export interface BalanceSummaryViewProps {
  currentRiskData: any;

  currentTotals: any;

  currentPeriodLabel: string;

  previousRiskData?: any;

  previousTotals?: any;

  previousPeriodLabel?: string;

  onViewFullDetail: () => void;

  isPdfMode?: boolean;
}

const BalanceSummaryView: React.FC<BalanceSummaryViewProps> = ({
  currentRiskData,

  currentTotals,

  currentPeriodLabel,

  previousRiskData,

  previousTotals,

  previousPeriodLabel,

  onViewFullDetail,

  isPdfMode = false,
}) => {
  const { clientTheme } = useClientTheme();
  const hasPrevious = !!previousRiskData && !!previousTotals;

  const getExerciseResultVariant = (
    value: string | number
  ): 'primary' | 'success' | 'danger' => {
    if (toNumber(value) < 0) return 'danger';
    return clientTheme ? 'primary' : 'success';
  };

  const config = JSON.parse(localStorage.getItem('config') ?? '{}');

  const isResultadoBrutoFiltered =
    config?.isResultadoBrutoFilteredEnabled ?? false;

  const isComplementaryDataEnabled = config?.isComplementaryDataEnabled ?? true;

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const isSectionExpanded = (key: string) => isPdfMode || !!expanded[key];

  const toggle = (key: string) => {
    if (isPdfMode) return;

    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const currTransformed = currentRiskData
    ? transformObject(currentRiskData)
    : [];

  const prevTransformed = previousRiskData
    ? transformObject(previousRiskData)
    : [];

  const filterGrossResultRows = (
    rows: Array<{
      label: string;
      valuePrev: string | number;
      valueCurr: string | number;
    }>
  ) => {
    if (!isResultadoBrutoFiltered) return rows;

    return rows.filter(
      (r) =>
        r.label !== 'agricultural_production' && r.label !== 'production_cost'
    );
  };

  const appendExpandedResultRows = (rows: SummaryRow[]) => {
    RESULT_SECTIONS.forEach((section) => {
      let merged = getMergedValues(
        currTransformed,
        prevTransformed,
        section.category
      );

      if (section.category === 'gross_result') {
        merged = filterGrossResultRows(merged);
      }

      merged.forEach((r) => {
        rows.push({
          label: `    ${getTranslation(section.category, r.label)}`,

          valuePrev: r.valuePrev,

          valueCurr: r.valueCurr,
        });
      });

      rows.push({
        label: `    ${section.subtotalLabel}`,

        valuePrev: section.getSubtotalPrev(previousTotals),

        valueCurr: section.getSubtotalCurr(currentTotals),

        isBold: true,

        isSectionSubtotal: true,
      });
    });

    const taxRows = getMergedValues(currTransformed, prevTransformed, 'taxes');

    taxRows.forEach((r) => {
      rows.push({
        label: `    ${getTranslation('taxes', r.label)}`,

        valuePrev: r.valuePrev,

        valueCurr: r.valueCurr,
      });
    });
  };

  const hasComplementaryData =
    isComplementaryDataEnabled &&
    (currentRiskData?.purchases != null ||
      currentRiskData?.amortization_depreciation != null ||
      previousRiskData?.purchases != null ||
      previousRiskData?.amortization_depreciation != null);

  const buildRows = (): SummaryRow[] => {
    const rows: SummaryRow[] = [];

    // --- ACTIVO TOTAL ---

    rows.push({
      label: 'Activo Total',

      valuePrev: previousTotals?.total_current ?? 0,

      valueCurr: currentTotals?.total_current ?? 0,

      isBold: true,
    });

    // Activo Corriente (expandible)

    rows.push({
      label: '  Activo Corriente',

      valuePrev: sumCategory(previousRiskData, 'current_assets'),

      valueCurr: sumCategory(currentRiskData, 'current_assets'),

      isBold: true,
    });

    if (isSectionExpanded('current_assets')) {
      const merged = getMergedValues(
        currTransformed,
        prevTransformed,
        'current_assets'
      );

      merged.forEach((r) => {
        rows.push({
          label: `    ${getTranslation('current_assets', r.label)}`,

          valuePrev: r.valuePrev,

          valueCurr: r.valueCurr,
        });
      });
    }

    // Activo No Corriente (expandible)

    rows.push({
      label: '  Activo No Corriente',

      valuePrev: sumCategory(previousRiskData, 'non_current_assets'),

      valueCurr: sumCategory(currentRiskData, 'non_current_assets'),

      isBold: true,
    });

    if (isSectionExpanded('non_current_assets')) {
      const merged = getMergedValues(
        currTransformed,
        prevTransformed,
        'non_current_assets'
      );

      merged.forEach((r) => {
        rows.push({
          label: `    ${getTranslation('non_current_assets', r.label)}`,

          valuePrev: r.valuePrev,

          valueCurr: r.valueCurr,
        });
      });
    }

    // --- PASIVO TOTAL ---

    rows.push({
      label: 'Pasivo Total',

      valuePrev: previousTotals?.total_non_current ?? 0,

      valueCurr: currentTotals?.total_non_current ?? 0,

      isBold: true,
    });

    // Pasivo Corriente (expandible)

    rows.push({
      label: '  Pasivo Corriente',

      valuePrev: sumCategory(previousRiskData, 'current_liabilities'),

      valueCurr: sumCategory(currentRiskData, 'current_liabilities'),

      isBold: true,
    });

    if (isSectionExpanded('current_liabilities')) {
      const merged = getMergedValues(
        currTransformed,
        prevTransformed,
        'current_liabilities'
      );

      merged.forEach((r) => {
        rows.push({
          label: `    ${getTranslation('current_liabilities', r.label)}`,

          valuePrev: r.valuePrev,

          valueCurr: r.valueCurr,
        });
      });
    }

    // Pasivo No Corriente (expandible, skip first sub-item)

    rows.push({
      label: '  Pasivo No Corriente',

      valuePrev: sumCategory(previousRiskData, 'non_current_liabilities'),

      valueCurr: sumCategory(currentRiskData, 'non_current_liabilities'),

      isBold: true,
    });

    if (isSectionExpanded('non_current_liabilities')) {
      const merged = getMergedValues(
        currTransformed,
        prevTransformed,
        'non_current_liabilities'
      );

      merged.slice(1).forEach((r) => {
        rows.push({
          label: `    ${getTranslation('non_current_liabilities', r.label)}`,

          valuePrev: r.valuePrev,

          valueCurr: r.valueCurr,
        });
      });
    }

    // --- PATRIMONIO NETO (sección independiente) ---

    rows.push({
      label: 'Patrimonio Neto',

      valuePrev: previousTotals?.total_net_worth ?? 0,

      valueCurr: currentTotals?.total_net_worth ?? 0,

      isBold: true,

      hasTopDivider: true,

      expandableKey: 'net_worth',
    });

    if (isSectionExpanded('net_worth')) {
      const merged = getMergedValues(
        currTransformed,
        prevTransformed,
        'net_worth'
      );

      merged.forEach((r) => {
        rows.push({
          label: `    ${getTranslation('net_worth', r.label)}`,

          valuePrev: r.valuePrev,

          valueCurr: r.valueCurr,
        });
      });
    }

    rows.push({
      label: 'Total pasivo + Patrimonio neto',

      valuePrev: previousTotals?.total_net_worth_and_non_current ?? 0,

      valueCurr: currentTotals?.total_net_worth_and_non_current ?? 0,

      isBold: true,
    });

    rows.push({
      label: 'Resultado del ejercicio',

      valuePrev: previousTotals?.taxes ?? 0,

      valueCurr: currentTotals?.taxes ?? 0,

      isBold: true,

      isExerciseResult: true,

      expandableKey: 'exercise_result',
    });

    if (isSectionExpanded('exercise_result')) {
      appendExpandedResultRows(rows);
    }

    if (hasComplementaryData) {
      rows.push({
        label: 'Datos Complementarios',

        valuePrev: '',

        valueCurr: '',

        isBold: true,

        expandableKey: 'complementary_data',

        hideAmounts: true,
      });

      if (isSectionExpanded('complementary_data')) {
        const hasPurchases =
          currentRiskData?.purchases != null ||
          previousRiskData?.purchases != null;

        const hasAmortDepr =
          currentRiskData?.amortization_depreciation != null ||
          previousRiskData?.amortization_depreciation != null;

        if (hasPurchases) {
          rows.push({
            label: '    Compras',

            valuePrev:
              previousRiskData?.purchases != null
                ? toNumber(previousRiskData.purchases)
                : '',

            valueCurr:
              currentRiskData?.purchases != null
                ? toNumber(currentRiskData.purchases)
                : '',
          });
        }

        if (hasAmortDepr) {
          rows.push({
            label: '    Amortizaciones y Depreciaciones',

            valuePrev:
              previousRiskData?.amortization_depreciation != null
                ? toNumber(previousRiskData.amortization_depreciation)
                : '',

            valueCurr:
              currentRiskData?.amortization_depreciation != null
                ? toNumber(currentRiskData.amortization_depreciation)
                : '',
          });
        }
      }
    }

    return rows;
  };

  const isExpandableRow = (row: SummaryRow): string | null => {
    if (row.expandableKey) return row.expandableKey;

    const trimmed = row.label.trim();

    const group = EXPANDABLE_GROUPS.find((g) => g.label === trimmed);

    return group ? group.key : null;
  };

  const renderAmountCell = (row: SummaryRow, value: string | number) => {
    if (row.hideAmounts) return null;

    if (value === '' || value === null || value === undefined) return null;

    const variant = row.isExerciseResult
      ? getExerciseResultVariant(value)
      : 'neutral';

    return (
      <Tooltip position='top' msg={String(value)}>
        <Span
          type={row.isBold ? '3' : '4'}
          variant={variant}
          weight={row.isBold ? 'bold' : 'regular'}
        >
          $ {formatCurrency(value ?? 0, true)}
        </Span>
      </Tooltip>
    );
  };

  const columns = [
    {
      name: 'Rubro',

      selector: (row: SummaryRow) => {
        const groupKey = isExpandableRow(row);

        if (row.expandableKey) {
          const labelVariant = row.isExerciseResult
            ? getExerciseResultVariant(row.valueCurr)
            : 'neutral';

          return (
            <div
              style={{
                display: 'flex',

                alignItems: 'center',

                gap: '8px',

                cursor: isPdfMode ? 'default' : 'pointer',

                width: '100%',
              }}
              onClick={() => toggle(groupKey as string)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ')
                  toggle(groupKey as string);
              }}
            >
              <P type='3' variant={labelVariant} weight='bold'>
                {row.label}
              </P>

              {!isPdfMode && (
                <Icon
                  name={
                    isSectionExpanded(groupKey as string)
                      ? 'chevron_up_outline'
                      : 'chevron_down_outline'
                  }
                  variant='neutral'
                  cursor={isPdfMode ? 'default' : 'pointer'}
                  width={16}
                  height={16}
                />
              )}
            </div>
          );
        }

        if (groupKey) {
          return (
            <div
              style={{
                display: 'flex',

                alignItems: 'center',

                gap: '8px',

                cursor: isPdfMode ? 'default' : 'pointer',

                width: '100%',
              }}
              onClick={() => toggle(groupKey)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') toggle(groupKey);
              }}
            >
              {!isPdfMode && (
                <Icon
                  name={
                    isSectionExpanded(groupKey)
                      ? 'chevron_up_outline'
                      : 'chevron_down_outline'
                  }
                  variant='neutral'
                  cursor={isPdfMode ? 'default' : 'pointer'}
                  width={16}
                  height={16}
                />
              )}

              <P type='3' variant='neutral' weight='bold'>
                {row.label.trim()}
              </P>
            </div>
          );
        }

        return (
          <P
            type={row.isBold ? '3' : '4'}
            variant='neutral'
            weight={row.isBold ? 'bold' : 'regular'}
          >
            {row.label}
          </P>
        );
      },

      grow: 2,
    },

    {
      name: currentPeriodLabel,

      selector: (row: SummaryRow) => renderAmountCell(row, row.valueCurr),

      right: true,
    },

    ...(hasPrevious
      ? [
          {
            name: previousPeriodLabel ?? 'Período anterior',

            selector: (row: SummaryRow) => renderAmountCell(row, row.valuePrev),

            right: true,
          },
        ]
      : []),
  ];

  const allRows = buildRows();

  return (
    <Card display='flex' flexDirection='column' gap='16px'>
      <Card
        style={{
          backgroundColor: '#F5F7FA',

          borderRadius: '8px',

          padding: '16px',
        }}
      >
        <SummaryWrapper data-testid='balance-summary-table-wrapper'>
          <style>
            {`

              .rdt_TableHead .rdt_TableCol:first-child,

              .rdt_TableRow .rdt_TableCell:first-child {

                padding-left: ${TABLE_EDGE_PADDING} !important;

              }

              .rdt_TableHead .rdt_TableCol:last-child,

              .rdt_TableRow .rdt_TableCell:last-child {

                padding-right: ${TABLE_EDGE_PADDING} !important;

              }

            `}
          </style>

          <Table
            columns={columns}
            tableData={{ size: allRows.length, totalElements: allRows.length }}
            data={allRows}
            variant='weak'
            disablePagination
            conditionalRowStyles={[
              {
                when: (row: SummaryRow) => !!row.hasTopDivider,

                style: {
                  borderTop: '1px solid #DFE6EB',
                },
              },
            ]}
          />
        </SummaryWrapper>
      </Card>

      {!isPdfMode && (
        <Card display='flex' justifyContent='flex-end'>
          <Button variant='secondary' size='md' onClick={onViewFullDetail}>
            Ver detalle completo
          </Button>
        </Card>
      )}
    </Card>
  );
};

export default BalanceSummaryView;
